import { useState, useMemo, useCallback } from "react";
import { sendReport } from "../../../utils/reporter";
import { RESERVE_STEP_INDEX } from "../config/constants";
import useBaseInfo from "../store/useBaseInfo";
import useBasicFormData from "../store/useBasicFormData";
import usePickupAddress from "../store/usePickupAddress";
import useReserveStepDocumentTitle from "./useReserveStepDocumentTitle";
import useReserveTimeInfo from "./useReserveTimeInfo";

type StepCallback = (newStep: number, currentStep: number) => void;

const useReserveStep = ({ reserveItems }) => {
  const { baseInfo } = useBaseInfo();

  const {
    basicFormData: {
      contactInfoStepData: {
        contactName,
        contactPhone,
        hasPickupServiceRight,
        usePickupService,
        pickupServiceTime,
      }
    },
  } = useBasicFormData();

  const {
    pickupAddress: { drivingPickupAddress },
  } = usePickupAddress();

  const reserveTimeInfo = useReserveTimeInfo();

  const [currentStep, setCurrentStep] = useState(
    RESERVE_STEP_INDEX.RESERVE_ITEMS
  );

  const canGoNextStep = useMemo(() => {
    const checked = !!reserveItems.find((item) => item.checked);
    // step 1
    if (currentStep === RESERVE_STEP_INDEX.RESERVE_ITEMS && checked) {
      return true;
    }

    // step 2
    if (
      currentStep === RESERVE_STEP_INDEX.TIME_STORE &&
      reserveTimeInfo?.startTime
    ) {
      return true;
    }

    // step 3
    if (
      currentStep === RESERVE_STEP_INDEX.CONTACT_INFO &&
      contactName &&
      contactPhone?.length === 11
    ) {
      if (hasPickupServiceRight && usePickupService) {
        // 取送车时间+地址+车牌
        return (
          !!pickupServiceTime &&
          !!drivingPickupAddress &&
          !!(baseInfo?.plateNumber || baseInfo?.addedPlateNumber)
        );
      } else {
        return true;
      }
    }

    return false;
  }, [
    reserveItems,
    currentStep,
    reserveTimeInfo,
    contactName,
    contactPhone,
    hasPickupServiceRight,
    usePickupService,
    pickupServiceTime,
    drivingPickupAddress,
    baseInfo,
  ]);

  const prev = useCallback(
    (callback?: StepCallback) => {
      const newStep =
        currentStep - 1 <= RESERVE_STEP_INDEX.RESERVE_ITEMS
          ? RESERVE_STEP_INDEX.RESERVE_ITEMS
          : currentStep - 1;

      setCurrentStep(newStep);
      callback?.(newStep, currentStep);

      if (currentStep === RESERVE_STEP_INDEX.CONTACT_INFO) {
        sendReport({
          mcode: "联系信息的上一步点击",
          point: "P11189`B010",
        });
      } else if (currentStep === RESERVE_STEP_INDEX.TIME_STORE) {
        sendReport({
          mcode: "选择门店的上一步点击",
          point: "P11183`B015",
        });
      }
    },
    [currentStep]
  );

  const next = useCallback(
    (callback?: StepCallback) => {
      if (!canGoNextStep) return;
      const newStep =
        currentStep + 1 <= RESERVE_STEP_INDEX.CONTACT_INFO
          ? currentStep + 1
          : RESERVE_STEP_INDEX.CONTACT_INFO;

      setCurrentStep(newStep);
      callback?.(newStep, currentStep);

      if (currentStep === RESERVE_STEP_INDEX.RESERVE_ITEMS) {
        sendReport({
          mcode: "选择预约项目后下一步点击",
          point: "P11183`B005",
        });
      } else if (currentStep === RESERVE_STEP_INDEX.TIME_STORE) {
        sendReport({
          mcode: "选择门店的下一步点击",
          point: "P11183`B016",
        });
      }
    },
    [canGoNextStep, currentStep]
  );

  useReserveStepDocumentTitle(currentStep);

  return {
    currentStep,
    prev,
    next,
    canGoNextStep,
  };
};

export default useReserveStep;
